import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createBillingMailTemplate,
  deleteBillingTemplate,
  getAllBillingTemplate,
  getAllBillingTemplateV2,
  getAllTypeSpecificBillingTemplate,
  getBillingTemplates,
  updateBillingMailTemplate,
} from "controller/api/configurations/billingAndContracts/billingTemplate";
import type {
  ApiErrorType,
  BillingTemplateListItem,
  GetBillingTemplateInput,
  GetBillingTemplatesInput,
  GetQueryProps,
  GetTypeSpecificBillingTemplateInput,
} from "shared";
import { sentryLogger } from "utilz";

export const useGetAllBillingTemplate = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
}: GetQueryProps) => {
  return useQuery({
    queryKey: [
      "billingTemplates",
      perPageCount,
      pageNumber,
      filter,
      searchQuery,
    ],
    queryFn: () =>
      getAllBillingTemplate({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getAllBillingTemplate",
      });
    },
  });
};

export const useGetAllBillingTemplateV2 = (
  {
    perPageCount,
    pageNumber,
    sortColumn,
    sortDirection,
    filter,
    searchQuery,
    customer_id,
  }: GetBillingTemplateInput,
  // Opt-out for callers that only sometimes need the list. Default: on.
  options?: { enabled?: boolean },
) => {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: [
      "billingTemplates",
      perPageCount,
      pageNumber,
      filter,
      searchQuery,
      customer_id,
    ],
    queryFn: () =>
      getAllBillingTemplateV2({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
        customer_id,
      }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getAllBillingTemplate",
      });
    },
  });
};

export const useGetTypeSpecificBillingTemplate = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  type,
}: GetTypeSpecificBillingTemplateInput) => {
  return useQuery({
    queryKey: [
      "billingTemplates",
      type,
      perPageCount,
      pageNumber,
      filter,
      searchQuery,
    ],
    queryFn: () =>
      getAllTypeSpecificBillingTemplate({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
        type,
      }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getAllBillingTemplate",
      });
    },
  });
};

const BILLING_TEMPLATES_PAGE_SIZE = 50;

/**
 * Both template types as one infinite list (BT-1,
 * `GET /v1/configuration/billing/templates`), for the tab-less Templates grid.
 * Rows are lightweight — resolve the full row with
 * `useFetchBillingTemplateDetail` before editing.
 */
export const useGetBillingTemplates = ({
  types,
  searchQuery,
}: GetBillingTemplatesInput) => {
  // Order-independent key, and whitespace-only search is no search.
  const sortedTypes = [...types].sort();
  const trimmedSearch = searchQuery?.trim() || undefined;

  return useInfiniteQuery({
    queryKey: ["billingTemplates", "unified", sortedTypes, trimmedSearch],
    queryFn: ({ pageParam = 1 }) =>
      getBillingTemplates({
        types: sortedTypes,
        searchQuery: trimmedSearch,
        pageNumber: pageParam,
        perPageCount: BILLING_TEMPLATES_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce(
        (count, page) => count + page.result.length,
        0,
      );
      return loaded < lastPage.total_count ? lastPage.page + 1 : undefined;
    },
    // Keep the grid on screen while a new search / filter loads.
    keepPreviousData: true,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getBillingTemplates",
      });
    },
  });
};

/**
 * Returns a fetcher for a template's FULL row — the payload the edit drawer
 * seeds from. The unified list omits taxes, discounts, emails and recipients,
 * so seeding from it would PATCH blanks over them on save.
 *
 * There is no detail GET yet (BT-4), so this reads the per-type list, narrowed
 * by name, and falls back to the unfiltered list if the name search misses.
 */
export const useFetchBillingTemplateDetail = () => {
  const queryClient = useQueryClient();

  return async (
    template: BillingTemplateListItem,
  ): Promise<BillingTemplateListItem> => {
    const findRow = async (searchQuery?: string) => {
      const response = await getAllTypeSpecificBillingTemplate({
        type: template.type,
        searchQuery,
        filter: [],
        sortColumn: "template_name",
        sortDirection: "asc",
        perPageCount: 1000,
        pageNumber: 1,
      });
      const rows: Omit<BillingTemplateListItem, "type">[] =
        response?.result ?? [];
      return rows.find((row) => row.mapping_id === template.mapping_id);
    };

    try {
      return await queryClient.fetchQuery({
        queryKey: [
          "billingTemplates",
          "detail",
          template.type,
          template.mapping_id,
        ],
        queryFn: async () => {
          const row =
            (await findRow(template.template_name)) ?? (await findRow());
          if (!row) {
            throw new Error("This template no longer exists");
          }
          return { ...row, type: template.type };
        },
      });
    } catch (error) {
      sentryLogger({
        error: error as ApiErrorType,
        workflow: "controlCenter",
        level: "error",
        functionName: "getAllTypeSpecificBillingTemplate",
      });
      throw error;
    }
  };
};

export const useCreateBillingTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => createBillingMailTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billingTemplates"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "createBillingMailTemplate",
      });
    },
  });
};

export const useDeleteBillingTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { invoice_template_ids: string[]; type: string }) =>
      deleteBillingTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billingTemplates"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "deleteBillingTemplate",
      });
    },
  });
};

export const useUpdateBillingTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => updateBillingMailTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billingTemplates"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateBillingMailTemplate",
      });
    },
  });
};
